import { MonitoringItem } from './Components/MonitoringTable/types';
import { MonitoramentoDetalheDTO, MonitoramentoDTO, MonitoramentoPeriodo, MonitoramentoRegra, MonitoramentoTipo } from './types';

const MONITORING_TYPE_LABELS: Record<MonitoramentoTipo, string> = {
  [MonitoramentoTipo.COMPETITIVIDADE_PRODUTO]: 'Competitividade de Produto',
  [MonitoramentoTipo.COMPETITIVIDADE_VAREJO]: 'Competitividade de Varejo',
};

const PERIOD_LABELS: Record<MonitoramentoPeriodo, string> = {
  [MonitoramentoPeriodo.ULTIMAS_4_SEMANAS]: 'Últimas 4 semanas',
  [MonitoramentoPeriodo.ULTIMAS_8_SEMANAS]: 'Últimas 8 semanas',
  [MonitoramentoPeriodo.ULTIMAS_12_SEMANAS]: 'Últimas 12 semanas',
};

export const humanizePeriod = (periodo: string): string => PERIOD_LABELS[periodo as MonitoramentoPeriodo] ?? periodo;

const mapRegrasToRanges = (regras: MonitoramentoRegra[] | undefined): MonitoringItem['ranges'] =>
  regras?.filter(regra => regra.ativo).map(regra => ({ tipo: regra.tipo, valorMin: regra.valorMin, valorMax: regra.valorMax }));

export const mapMonitoramentoToMonitoringItem = (dto: MonitoramentoDTO): MonitoringItem => {
  const cidades = dto.cidades ?? [];
  const ufs = dto.ufs?.filter((uf): uf is string => Boolean(uf)) ?? [];
  const isCidade = cidades.length > 0;

  return {
    id: dto.id,
    name: dto.nome,
    type: MONITORING_TYPE_LABELS[dto.tipo] ?? dto.tipo,
    ativo: dto.ativo,
    isVarejo: dto.tipo === MonitoramentoTipo.COMPETITIVIDADE_VAREJO,
    details: {
      productName: dto.produtoReferencia?.produtoNome ?? '-',
      competitorsCount: dto.totalProdutosConcorrentes,
      competitorProducts: [],
      allProducts: [],
      tipoComparacao: 'PRODUTO',
      categorias: [],
      secoes: [],
      networksCount: dto.totalRedes,
      citiesCount: isCidade ? cidades.length : ufs.length ? ufs.length : dto.totalCidades,
      cities: isCidade ? cidades : ufs,
      tipoLocal: dto.todoTerritorio ? 'Todo o território' : isCidade ? 'Cidade' : 'UF',
      period: humanizePeriod(dto.periodo),
      redeReferenciaNome: dto.redeReferencia?.redeNome,
    },
    ranges: mapRegrasToRanges(dto.regras),
    competitivenessIndex: dto.indiceCompetitividade,
    compartilhado: dto.tipoVinculo === 'COMPARTILHADO',
    compartilhadoPor: dto.compartilhadoPorLogin,
  };
};

const mapLocalizacao = (detalhe: MonitoramentoDetalheDTO): { tipoLocal: string; cities: string[] } => {
  if (detalhe.todoTerritorio) {
    return { tipoLocal: 'Todo o território', cities: [] };
  }

  const ufEntries = detalhe.cidades.filter(cidade => !cidade.cidadeNome && cidade.uf);
  if (ufEntries.length > 0) {
    return { tipoLocal: 'UF', cities: ufEntries.map(cidade => cidade.uf.replace(/_/g, ' ')) };
  }

  return { tipoLocal: 'Cidade', cities: detalhe.cidades.map(cidade => cidade.cidadeNome) };
};

export const mapMonitoramentoDetalheToMonitoringItem = (detalhe: MonitoramentoDetalheDTO): MonitoringItem => {
  const produtoReferencia = detalhe.produtos.find(produto => produto.tipo === 'REFERENCIA');
  const produtosConcorrentes = detalhe.produtos.filter(produto => produto.tipo === 'CONCORRENTE');
  const localizacao = mapLocalizacao(detalhe);
  // Mesma convenção usada em useCriarMonitoramentoForm/analiseCompetitividadeMapper: o GET não tem um
  // campo próprio pra "tipoComparacao", deriva a partir de categorias/secoes preenchidos.
  const tipoComparacao = detalhe.categorias.length > 0 ? 'CATEGORIA' : detalhe.secoes.length > 0 ? 'SECAO' : 'PRODUTO';

  return {
    id: detalhe.id,
    name: detalhe.nome,
    type: MONITORING_TYPE_LABELS[detalhe.tipo] ?? detalhe.tipo,
    ativo: detalhe.ativo,
    isVarejo: detalhe.tipo === MonitoramentoTipo.COMPETITIVIDADE_VAREJO,
    details: {
      productName: produtoReferencia?.produtoNome ?? '-',
      competitorsCount: produtosConcorrentes.length,
      competitorProducts: produtosConcorrentes.map(produto => ({ ean: produto.produtoEan, name: produto.produtoNome })),
      allProducts: detalhe.produtos.map(produto => ({ ean: produto.produtoEan, name: produto.produtoNome })),
      tipoComparacao,
      categorias: detalhe.categorias,
      secoes: detalhe.secoes,
      networksCount: detalhe.redes.length,
      citiesCount: detalhe.cidades.length,
      cities: localizacao.cities,
      tipoLocal: localizacao.tipoLocal,
      period: humanizePeriod(detalhe.periodo),
    },
    ranges: mapRegrasToRanges(detalhe.regras),
    competitivenessIndex: detalhe.indiceCompetitividade,
    compartilhado: detalhe.tipoVinculo === 'COMPARTILHADO',
    compartilhadoPor: detalhe.compartilhadoPorLogin,
  };
};
